import { HttpErrorResponse } from '@angular/common/http';
import { tapResponse } from '@ngrx/operators';
import {
  EmptyFeatureResult,
  SignalStoreFeature,
  patchState,
  signalStoreFeature,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, Subject, pipe, switchMap, takeUntil, tap } from 'rxjs';

type IsLoadingKey<Name extends string> = `${Name}IsLoading`;
type ErrorStateKey<Name extends string> = `${Name}Error`;
type LoadStartKey<Name extends string> = `${Name}LoadStart$`;
type LoadSuccessKey<Name extends string> = `${Name}LoadSuccess$`;
type LoadErrorKey<Name extends string> = `${Name}LoadError$`;
type LoadMethodKey<Name extends string> = `${Name}Load`;

type ApiResourceState<Name extends string> = { [K in IsLoadingKey<Name>]: boolean } & { [K in ErrorStateKey<Name>]: string | undefined };

type ApiResourceProps<Name extends string, Input, T> = { [K in LoadStartKey<Name>]: Subject<Input> } & {
  [K in LoadSuccessKey<Name>]: Subject<T>;
} & { [K in LoadErrorKey<Name>]: Subject<unknown> };

type ApiResourceMethods<Name extends string, Input> = {
  [K in LoadMethodKey<Name>]: (input: Input | Observable<Input> | (() => Input)) => void;
};

/**
 * Reusable signal-store feature for "load a resource via an API call". Given a `name` and a
 * `loader` function, produces:
 * - `{name}IsLoading` / `{name}Error` state
 * - `{name}LoadStart$` / `{name}LoadSuccess$` / `{name}LoadError$` subjects, for other features
 *   (e.g. `withResourceWebsocket`, `withReloadOnReconnect`) or the consumer itself to react to
 * - a `{name}Load(input)` method that triggers the call (a new call cancels any in-flight one)
 *
 * Deliberately does NOT store the loaded value in state — consumers that need more than the raw
 * value (e.g. recomputing derived filter options) tap `{name}LoadSuccess$` and `patchState` their
 * own domain fields themselves. `Input`/`T` are inferred from `loader` at the call site.
 *
 * `withApiResource(...)` itself is typically called at module-eval time (as a direct argument to
 * `signalStoreFeature(...)`), before any injection context exists, and `loader` later runs inside an
 * async `switchMap` callback — NOT inside one either. So `loader` must not call `inject()` itself;
 * any services it needs should be resolved once via `inject()` in a `withFeature((store) => {...})`
 * wrapper (which runs synchronously during store construction, in context) and captured by closure:
 *
 * ```ts
 * withFeature(() => {
 *   const service = inject(MyService);
 *   return withApiResource('thing', (input) => service.getThing$(input));
 * })
 * ```
 *
 * `clear$` is a manual "stop everything and reset to default" signal (e.g. `withCancellation('clear')`
 * composed earlier on the same store) — NOT a substitute for `withApiResource`'s own destroy cleanup,
 * but a separate, explicitly-triggered one. Some consuming stores are not reliably destroyed when
 * their owning screen is (e.g. persisted route-level stores across param changes), so an in-flight
 * call must also be cancellable on demand. On `clear$`: the in-flight call (if any) is cancelled, and
 * `{name}IsLoading`/`{name}Error` are reset to their defaults. The `{name}Load` method itself keeps
 * working afterwards — only the current in-flight call is torn down, not the outer pipe.
 *
 * Note on implementation: internally this casts through `Record<string, unknown>` rather than the
 * precise `ApiResourceProps`/`ApiResourceState` mapped types — intersecting several independently
 * keyed generic mapped types and then indexing by a generic key defeats TypeScript's inference here.
 * The precise types are still fully enforced at the function's declared return type, which is what
 * consumers (and other features composed after this one) actually see.
 */
export function withApiResource<Name extends string, Input, T>(
  name: Name,
  loader: (input: Input) => Observable<T>,
  clear$: Observable<void>
): SignalStoreFeature<
  EmptyFeatureResult,
  { state: ApiResourceState<Name>; props: ApiResourceProps<Name, Input, T>; methods: ApiResourceMethods<Name, Input> }
> {
  const isLoadingKey = `${name}IsLoading`;
  const errorKey = `${name}Error`;
  const loadStartKey = `${name}LoadStart$`;
  const loadSuccessKey = `${name}LoadSuccess$`;
  const loadErrorKey = `${name}LoadError$`;
  const loadMethodKey = `${name}Load`;

  const feature = signalStoreFeature(
    withState(() => ({ [isLoadingKey]: false, [errorKey]: undefined }) as Record<string, unknown>),
    withProps(() => ({
      [loadStartKey]: new Subject<Input>(),
      [loadSuccessKey]: new Subject<T>(),
      [loadErrorKey]: new Subject<HttpErrorResponse>(),
    })),
    withMethods((store) => {
      const props = store;
      const loadStart$ = props[loadStartKey] as Subject<Input>;
      const loadSuccess$ = props[loadSuccessKey] as Subject<T>;
      const loadError$ = props[loadErrorKey] as Subject<HttpErrorResponse>;

      const load = rxMethod<Input>(
        pipe(
          tap((input) => {
            loadStart$.next(input);
            patchState(store, { [isLoadingKey]: true, [errorKey]: undefined } as Record<string, unknown>);
          }),
          switchMap((input) =>
            loader(input).pipe(
              tapResponse({
                next: (result) => {
                  patchState(store, { [isLoadingKey]: false } as Record<string, unknown>);
                  loadSuccess$.next(result);
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, { [isLoadingKey]: false, [errorKey]: `Failed to load ${name}` } as Record<string, unknown>);
                  loadError$.next(error);
                },
              }),
              takeUntil(clear$)
            )
          )
        )
      );

      // Resets IsLoading/Error back to defaults whenever the shared clear$ fires — the in-flight call
      // itself is already cancelled by the takeUntil(clear$) above; this just resets the visible state.
      const resetOnClear = rxMethod<void>(
        pipe(tap(() => patchState(store, { [isLoadingKey]: false, [errorKey]: undefined } as Record<string, unknown>)))
      );
      resetOnClear(clear$);

      return { [loadMethodKey]: load } as Record<string, (input: Input | Observable<Input> | (() => Input)) => void>;
    }),
    withHooks({
      onDestroy(store) {
        const props = store as unknown as Record<string, Subject<unknown>>;
        props[loadStartKey].complete();
        props[loadSuccessKey].complete();
        props[loadErrorKey].complete();
      },
    })
  );

  return feature as unknown as SignalStoreFeature<
    EmptyFeatureResult,
    { state: ApiResourceState<Name>; props: ApiResourceProps<Name, Input, T>; methods: ApiResourceMethods<Name, Input> }
  >;
}
